import {ButtonGroup, Icon, IconButton, Text} from "@chakra-ui/react";
import {ComponentProps, Dispatch, memo, SetStateAction, useCallback} from "react";
import {MagnifyingGlassPlusIcon, MinusIcon, PlusIcon} from "@heroicons/react/16/solid";
import {Tooltip} from "../../../../../../../../components/ui/tooltip";
import {Button} from "../../../../../../../../components/ui/button";
import {Camera} from "../../utility/camera";
import {useUiPointerEvents} from "../../utility/uiPointerEventsContext";

const TOOLTIP_POSITIONING: ComponentProps<typeof Tooltip>["positioning"] = {placement: "top"};

export const ZoomControl = memo(
  ({camera, setCamera}: {camera: Camera; setCamera: Dispatch<SetStateAction<Camera>>}) => {
    const pointerEvents = useUiPointerEvents();

    const incrementZoom = useCallback(() => {
      setCamera(prev => {
        const next = prev.clone();
        next.targetZoomStep += 1;
        return next;
      });
    }, [setCamera]);

    const decrementZoom = useCallback(() => {
      setCamera(prev => {
        const next = prev.clone();
        next.targetZoomStep -= 1;
        return next;
      });
    }, [setCamera]);

    const resetZoom = useCallback(() => {
      setCamera(prev => {
        const next = prev.clone();
        next.targetZoomStep = 0;
        return next;
      });
    }, [setCamera]);

    return (
      <ButtonGroup pointerEvents={pointerEvents} attached bg="white" shadow="md" overflow="hidden" rounded="md">
        <Tooltip content="Zoom out" positioning={TOOLTIP_POSITIONING}>
          <IconButton size="xs" variant="subtle" borderRightRadius={0} onClick={decrementZoom}>
            <MinusIcon />
          </IconButton>
        </Tooltip>
        <Tooltip content="Reset zoom" positioning={TOOLTIP_POSITIONING}>
          <Button size="xs" variant="ghost" onClick={resetZoom}>
            <Icon asChild color="fg.muted">
              <MagnifyingGlassPlusIcon />
            </Icon>
            <Text fontSize="xs" w={10} color="fg.muted" fontWeight="normal">
              {(camera.targetZoomFactor * 100).toFixed(0)}%
            </Text>
          </Button>
        </Tooltip>
        <Tooltip content="Zoom in" positioning={TOOLTIP_POSITIONING}>
          <IconButton size="xs" variant="subtle" borderLeftRadius={0} onClick={incrementZoom}>
            <PlusIcon />
          </IconButton>
        </Tooltip>
      </ButtonGroup>
    );
  },
);
